import {
  Bot,
  Sparkles,
  Code2,
  Plug,
  Database,
  MessageSquare,
  Smartphone,
  Globe,
  Workflow,
  Cloud,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { LanguageCode } from "../../utils/translations";
import { SERVICE_CONTENT_HI } from "./servicesDataHi";
import { SERVICE_CONTENT_FR } from "./servicesDataFr";

export interface ServiceHighlight {
  title: string;
  desc: string;
}

export interface ServiceEntry {
  slug: string;
  group: string;
  title: string;
  tagline: string;
  intro: string;
  icon: LucideIcon;
  highlights: ServiceHighlight[];
  deliverables: string[];
  useCases: string[];
}

/**
 * Single source of truth for every "beyond social media" service.
 * Add a new object here and it automatically gets a page at /services/:slug
 * and shows up on the /services index, the navbar menu and the home band.
 */
export const SERVICES: ServiceEntry[] = [
  {
    slug: "ai-development",
    group: "AI & Machine Learning",
    title: "AI Development",
    tagline: "Put AI to work inside your business.",
    intro:
      "We design and ship AI that solves a real job — answering customers, reading documents, spotting patterns or predicting what happens next. You get working software, not a science project.",
    icon: Sparkles,
    highlights: [
      {
        title: "Custom AI solutions",
        desc: "Built around your data and your workflow, not a generic template.",
      },
      {
        title: "Smart document reading",
        desc: "Turn invoices, forms and PDFs into clean, usable information.",
      },
      {
        title: "Predictions & insights",
        desc: "Forecast demand, spot fraud or flag risks before they cost you.",
      },
      {
        title: "Your data stays yours",
        desc: "Cloud or on-premise — you choose where your data lives.",
      },
    ],
    deliverables: [
      "Discovery workshop and a clear AI roadmap",
      "Working AI model or feature integrated into your product",
      "Dashboard to monitor accuracy and results",
      "Training and handover for your team",
    ],
    useCases: [
      "Automatically sort and reply to customer messages",
      "Read and digitise paperwork",
      "Forecast sales, stock or demand",
      "Recommend the right product to each customer",
    ],
  },
  {
    slug: "ai-agents",
    group: "AI & Machine Learning",
    title: "AI Agents",
    tagline: "Digital team-mates that finish the task.",
    intro:
      "Agents don't just answer — they act. They look up information, take the next step, update your systems and keep going until the job is done, around the clock.",
    icon: Bot,
    highlights: [
      {
        title: "Support agents",
        desc: "Handle common questions and pass tricky ones to a human.",
      },
      {
        title: "Sales agents",
        desc: "Qualify leads, book meetings and follow up automatically.",
      },
      {
        title: "Back-office agents",
        desc: "Update records, raise tickets and chase missing details.",
      },
      {
        title: "Connected to your tools",
        desc: "Agents work inside the apps you already use every day.",
      },
    ],
    deliverables: [
      "Agent designed around your real conversation flows",
      "Safe guardrails and human hand-off rules",
      "Knowledge base set up from your own content",
      "Reporting on conversations and outcomes",
    ],
    useCases: [
      "24/7 first-line customer support",
      "Instant lead qualification and booking",
      "Automated order and account enquiries",
      "Internal helpdesk for your own staff",
    ],
  },
  {
    slug: "api-development",
    group: "Software & Integrations",
    title: "API Development",
    tagline: "Connect systems so data moves itself.",
    intro:
      "We build the secure APIs and connections that let your apps, partners and tools talk to each other — so information stops being copied by hand.",
    icon: Code2,
    highlights: [
      {
        title: "REST & GraphQL APIs",
        desc: "Clean, well-documented endpoints your team can rely on.",
      },
      {
        title: "Third-party connections",
        desc: "Payments, messaging, maps, accounting and more.",
      },
      {
        title: "Secure by design",
        desc: "Keys, roles and audit trails built in from day one.",
      },
      {
        title: "Fast and reliable",
        desc: "Built to handle real traffic without falling over.",
      },
    ],
    deliverables: [
      "API design and documentation",
      "Development, testing and deployment",
      "Authentication and access control",
      "Ongoing monitoring and support",
    ],
    useCases: [
      "Sync orders between your shop and accounting",
      "Send leads from your website into your CRM",
      "Share data securely with partners",
      "Power your mobile app with one backend",
    ],
  },
  {
    slug: "crm-integration",
    group: "Software & Integrations",
    title: "CRM Deployment & Integration",
    tagline: "Every lead and customer in one place.",
    intro:
      "We set up, customise and connect your CRM so nothing slips through. Leads land in the right pipeline, follow-ups happen on time and your team sees one clear picture.",
    icon: Database,
    highlights: [
      {
        title: "Set-up & migration",
        desc: "Move your existing contacts over without losing history.",
      },
      {
        title: "Made for your process",
        desc: "Pipelines, stages and fields shaped around how you sell.",
      },
      {
        title: "Automated follow-ups",
        desc: "Reminders, tasks and messages triggered automatically.",
      },
      {
        title: "Connected everywhere",
        desc: "Website, forms, email, SMS and social all feed in.",
      },
    ],
    deliverables: [
      "CRM configured for your sales process",
      "Data migration and clean-up",
      "Automated follow-up workflows",
      "Team training and adoption support",
    ],
    useCases: [
      "Never lose a lead from your website or ads",
      "Give every rep the same clear pipeline",
      "Trigger timely follow-ups automatically",
      "See your full sales picture on one dashboard",
    ],
  },
  {
    slug: "system-integrations",
    group: "Software & Integrations",
    title: "System Integrations",
    tagline: "Make all your tools work as one.",
    intro:
      "Most businesses run on a dozen disconnected apps. We link them together so data flows automatically instead of being re-typed between them.",
    icon: Plug,
    highlights: [
      {
        title: "End-to-end workflows",
        desc: "One action in one tool triggers the right step in another.",
      },
      {
        title: "No more double entry",
        desc: "Enter information once; it updates everywhere it should.",
      },
      {
        title: "Works with what you own",
        desc: "We connect the tools you already use before buying new ones.",
      },
      {
        title: "Reliable and monitored",
        desc: "You get alerts if a connection ever needs attention.",
      },
    ],
    deliverables: [
      "Map of your current tools and data flows",
      "Connections built between your key systems",
      "Error handling and notifications",
      "Documentation of how everything connects",
    ],
    useCases: [
      "Website enquiry → CRM → email follow-up",
      "Online order → invoicing → accounts",
      "Payment received → customer notified",
      "Form filled → task created for your team",
    ],
  },
  {
    slug: "cloud-infrastructure",
    group: "Cloud & Infrastructure",
    title: "Cloud, Servers & Infrastructure",
    tagline: "Reliable hosting, plus experts on demand.",
    intro:
      "From cloud deployments to dedicated servers and extra hands, we give your software a solid home and the infrastructure muscle it needs to grow.",
    icon: Cloud,
    highlights: [
      {
        title: "Cloud deployments",
        desc: "Set up and scale on AWS, Azure or Google Cloud.",
      },
      {
        title: "Servers & hardware",
        desc: "Provision, configure and harden the machines you run on.",
      },
      {
        title: "Hire infra resources",
        desc: "Bring in experienced DevOps and infrastructure engineers.",
      },
      {
        title: "Monitoring & backups",
        desc: "Know the moment something wobbles — and recover fast.",
      },
    ],
    deliverables: [
      "Infrastructure designed for your workload",
      "Automated deployment pipelines",
      "Monitoring, alerts and daily backups",
      "Security hardening and cost optimisation",
    ],
    useCases: [
      "Move your app to the cloud safely",
      "Add capacity for a busy season",
      "Cover a short-term gap in your IT team",
      "Keep critical systems online and backed up",
    ],
  },
  {
    slug: "messaging-services",
    group: "Communication",
    title: "SMS & Email Services",
    tagline: "Reach customers where they actually look.",
    intro:
      "Reliable, high-delivery SMS and email that plug straight into your workflows — reminders, receipts, offers and follow-ups, sent automatically at the right moment.",
    icon: MessageSquare,
    highlights: [
      {
        title: "SMS campaigns",
        desc: "Time-sensitive messages with delivery tracking.",
      },
      {
        title: "Email at scale",
        desc: "Newsletters, sequences and transactional email.",
      },
      {
        title: "Trigger-based sending",
        desc: "Messages fire off from your own business events.",
      },
      {
        title: "Templates & analytics",
        desc: "Branded templates with open and click reporting.",
      },
    ],
    deliverables: [
      "Sender setup and deliverability best practice",
      "Branded templates for SMS and email",
      "Automated sequences wired to your workflow",
      "Delivery, open and click reporting",
    ],
    useCases: [
      "Appointment and payment reminders",
      "Welcome and onboarding messages",
      "Promotions to your customer list",
      "Order and delivery notifications",
    ],
  },
  {
    slug: "website-development",
    group: "Apps & Automation",
    title: "Website Development",
    tagline: "Fast, modern sites that convert.",
    intro:
      "Marketing sites, landing pages and web apps built to load fast, look sharp and turn visitors into enquiries — wired into your CRM and analytics from day one.",
    icon: Globe,
    highlights: [
      {
        title: "Marketing & landing pages",
        desc: "Designed to inform and get the click.",
      },
      {
        title: "Custom web apps",
        desc: "Portals, dashboards and customer tools.",
      },
      {
        title: "Built to be found",
        desc: "Search-friendly structure and clean performance.",
      },
      {
        title: "Connected from launch",
        desc: "Forms, chat and tracking feeding your systems.",
      },
    ],
    deliverables: [
      "Design and build of your site or web app",
      "Mobile-first, responsive on every device",
      "Forms and chat connected to your CRM",
      "Analytics and launch support",
    ],
    useCases: [
      "A high-converting landing page for a campaign",
      "A refreshed marketing website",
      "A customer portal or booking tool",
      "An online quote or checkout flow",
    ],
  },
  {
    slug: "mobile-app-development",
    group: "Apps & Automation",
    title: "Mobile App Development",
    tagline: "Apps your customers and team love to use.",
    intro:
      "Native-feeling iOS and Android apps for your customers, staff or field teams — with one backend that keeps everyone in sync.",
    icon: Smartphone,
    highlights: [
      {
        title: "Customer apps",
        desc: "Booking, ordering, loyalty and account access.",
      },
      {
        title: "Staff & field apps",
        desc: "Jobs, checklists and updates from anywhere.",
      },
      {
        title: "One backend, two platforms",
        desc: "Build once and reach both iPhone and Android.",
      },
      {
        title: "Offline-friendly",
        desc: "Keeps working when the signal drops.",
      },
    ],
    deliverables: [
      "App design, build and store submission",
      "Shared backend and admin dashboard",
      "Push notifications and analytics",
      "Post-launch updates and support",
    ],
    useCases: [
      "A booking app for your customers",
      "A field team app for jobs and photos",
      "A loyalty or membership app",
      "An internal app for approvals on the go",
    ],
  },
  {
    slug: "mobile-workflow-automation",
    group: "Apps & Automation",
    title: "Mobile Workflow Automation",
    tagline: "Your processes, running from a phone.",
    intro:
      "Turn daily routines — approvals, reports, check-ins, job updates — into simple mobile workflows that run themselves and keep your team moving.",
    icon: Workflow,
    highlights: [
      {
        title: "Approvals on the go",
        desc: "Managers approve from a notification, not a desk.",
      },
      {
        title: "Job & task updates",
        desc: "Staff update status live from the field.",
      },
      {
        title: "Auto reports",
        desc: "Daily summaries built and sent automatically.",
      },
      {
        title: "Works with your tools",
        desc: "Feeds your CRM, sheets and accounting.",
      },
    ],
    deliverables: [
      "Workflows mapped from your current process",
      "Mobile forms and approval steps",
      "Automatic notifications and reports",
      "Integration with your existing systems",
    ],
    useCases: [
      "Field staff update jobs from their phones",
      "Managers approve spends or time off instantly",
      "Daily sales and visit reports sent automatically",
      "Checklists completed and logged in the field",
    ],
  },
];

export const SERVICE_GROUPS: string[] = Array.from(
  new Set(SERVICES.map((s) => s.group))
);

export const getServiceBySlug = (slug: string): ServiceEntry | undefined =>
  SERVICES.find((s) => s.slug === slug);

/**
 * Returns the service catalogue with human-readable fields localized.
 * Icons and slugs are always taken from the English source of truth.
 */
export const getServices = (lang: LanguageCode): ServiceEntry[] => {
  const content =
    lang === "hi"
      ? SERVICE_CONTENT_HI
      : lang === "fr"
      ? SERVICE_CONTENT_FR
      : undefined;

  if (!content) return SERVICES;

  return SERVICES.map((service) => {
    const localized = content[service.slug];
    return localized ? { ...service, ...localized } : service;
  });
};

export const getServiceBySlugLocalized = (
  slug: string,
  lang: LanguageCode
): ServiceEntry | undefined =>
  getServices(lang).find((s) => s.slug === slug);
